import { faAngleLeft, faAngleRight, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import styles from 'components/UI/V3/MediaViewer/MediaViewer.module.scss';
import { MediaViewerPlayer } from 'components/UI/V3/MediaViewer/Player/MediaViewerPlayer';
import { MediaViewerThumbnail } from 'components/UI/V3/MediaViewer/Thumbnail/MediaViewerThumbnail';
import React from 'react';
import { createPortal } from 'react-dom';

export interface Media {
    type: 'image' | 'video';
    src: string;
    thumbnailUrl?: string;
}

interface MediaViewerProps {
    media: Array<Media>;
    children?: React.ReactNode;
    onClose?: () => void;
    zIndex?: number;
}

export const MediaViewer: React.FC<MediaViewerProps> = ({ media, children, onClose, zIndex }) => {
    const [activeMedia, setActiveMedia] = React.useState<Media>(media[0]);

    const isLastMedia = React.useMemo(() => {
        return media.findIndex(item => item.src === activeMedia.src) === media.length - 1;
    }, [media, activeMedia]);

    const isFirstMedia = React.useMemo(() => {
        return media.findIndex(item => item.src === activeMedia.src) === 0;
    }, [media, activeMedia]);

    const prevMediaHandler = React.useCallback(() => {
        const currentIndex = media.findIndex(item => item.src === activeMedia.src);
        const prevIndex = (currentIndex - 1 + media.length) % media.length;

        if (currentIndex === 0) return;

        setActiveMedia(media[prevIndex]);
    }, [media, activeMedia]);

    const nextMediaHandler = React.useCallback(() => {
        const currentIndex = media.findIndex(item => item.src === activeMedia.src);
        const nextIndex = (currentIndex + 1) % media.length;

        if (currentIndex === media.length - 1) return;

        setActiveMedia(media[nextIndex]);
    }, [media, activeMedia]);

    React.useEffect(() => {
        const keydownHandler = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') {
                prevMediaHandler();
            } else if (event.key === 'ArrowRight') {
                nextMediaHandler();
            } else if (event.key === 'Escape') {
                onClose?.();
            }
        };

        document.addEventListener('keydown', keydownHandler);

        return () => {
            document.removeEventListener('keydown', keydownHandler);
        };
    }, [prevMediaHandler, nextMediaHandler, onClose]);

    return createPortal(
        <div
            className={styles.root}
            style={zIndex !== undefined ? { zIndex } : undefined}
        >
            <div className={styles.visualizer}>
                <button
                    className={styles.closeButton}
                    onClick={onClose}
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>

                <div
                    className={clsx(styles.visualizerControl, {
                        [styles.hidden]: media.length === 1
                    })}
                >
                    <button
                        disabled={isFirstMedia}
                        className={styles.visualizerControlButton}
                        onClick={prevMediaHandler}
                    >
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </button>
                </div>

                <div className={styles.visualizerWrapper}>
                    <MediaViewerPlayer media={activeMedia} />

                    <div className={styles.thumbnails}>
                        {media.map((item, i) => (
                            <MediaViewerThumbnail
                                media={item}
                                key={`${item.type}-${i}`}
                                active={activeMedia.src === item.src}
                                onClick={() => setActiveMedia(item)}
                            />
                        ))}
                    </div>
                </div>

                <div
                    className={clsx(styles.visualizerControl, {
                        [styles.hidden]: media.length === 1
                    })}
                >
                    <button
                        disabled={isLastMedia}
                        className={styles.visualizerControlButton}
                        onClick={nextMediaHandler}
                    >
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                </div>
            </div>

            {children && <div className={styles.content}>{children}</div>}
        </div>,
        document.body
    );
};
